/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Page SubscriberId component
 *
 * @author Yakov Gushchin
 * @author Andrey Dubov
 * @author Ilya Baranchikov
 * @version 1.25.1
 * @since 1.12.0
 */

import { FC, Fragment, useEffect } from 'react';

import { Button } from '@nexign/scc-ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@/main/modules/logging';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { EFormatData } from '@/main/pages/PageDashboard/types/widgetTypes';
import { formatDate } from '@/main/utils/formatText';
import { LinkBlock, SideMenuItem } from '@components/common';
import EllipsisText from '@components/common/EllipsisText/EllipsisText';
import { ISideMenuItem } from '@components/common/SideMenu/SideMenuItem';
import Title from '@components/common/Title/Title';
import { MapEditorTypeToLabelsApiPath } from '@components/Table/components/footerModal/LabelEditor/labelEditorConstants';
import { EEditableLabel } from '@components/Table/components/footerModal/LabelEditor/labelEditorTypes';
import { API_SUBSCRIBER_ID_PARAM, API_TABLE_SUBSCRIBER_ID, SUBSCRIBER_MOBILE } from '@modules/app/Api';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectPageSubscriberIdTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { Loader } from '@modules/loader/components/Loader';
import NoticeMessage from '@modules/notification/components/NoticeMessage';
import { getNotificationsStart } from '@modules/notification/notificationActions';
import { ENotificationPlace } from '@modules/notification/types/notificationTypes';
import ErrorPage from '@pages/ErrorPage/ErrorPage';
import { makeSelectFeatureEnabled, makeSelectUserTypeId } from '@pages/PageAuth/selectors/authSelectors';
import { CLIENT_ID, IRouteParams, RoutePath } from '@routes/constants/routesConstants';
import { icons as themeIcons } from '@theme/index';
import { stringFormatParams } from '@utils/stringFormat';

import { SubscriberIdContext } from '../context';
import { subscriberIdApi, useGetSubscriberIdMetaQuery } from '../store';
import { PageSubscriberIdDateStyled, PageSubscriberIdStyled, PageSubscriberIdTitleStyled } from './styled/PageSubscriberIdStyled';
import SubscriberIdFooter from './SubscriberIdFooter';

interface IPageSubscriberTabMenu extends Pick<ISideMenuItem, 'title'> {
    url: string;
    onClick?: ISideMenuItem['onClick'];
}

const PageSubscriberId: FC = ({ children }) => {
    const location = useLocation();

    const dispatch = useDispatch();

    const { clientId: subscriberId } = useParams<IRouteParams>();

    const translationSubscriberId = useSelector(makeSelectPageSubscriberIdTranslation());
    const translationCommon = useSelector(makeSelectTranslationCommon());
    const isSubsInRoamingEnabled = 
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.SUBSCRIBERS_IN_ROAMING_NOTIFICATION));

    const userType = useSelector(makeSelectUserTypeId());

    const { data, isFetching, isSuccess, error } = useGetSubscriberIdMetaQuery(
        { subscriberId, userType },
        {
            skip: !subscriberId,
        },
    );

    const isRoamInfoExist = !!data?.eventTimeChangeCountry && !!data?.currCountryName;

    const roamDateString = 
        isRoamInfoExist &&
        stringFormatParams(translationSubscriberId.pageTitle.roamText, {
            roamDate: formatDate(data?.eventTimeChangeCountry as string, { format: EFormatData.FullData }),
            roamCountry: data?.currCountryName,
        });

    useEffect(() => {
        return () => {
            dispatch(subscriberIdApi.util.resetApiState());
        };
    }, [dispatch]);

    useEffect(() => {
        dispatch(getNotificationsStart({ place: ENotificationPlace.subscriberId, params: { subscriberId } }));
    }, [dispatch, subscriberId]);

    useEffect(() => {
        MapEditorTypeToLabelsApiPath[EEditableLabel.subscriberId] = stringFormatParams(API_TABLE_SUBSCRIBER_ID, {
            [API_SUBSCRIBER_ID_PARAM]: subscriberId,
        });

        return () => {
            MapEditorTypeToLabelsApiPath[EEditableLabel.subscriberId] = SUBSCRIBER_MOBILE;
        };
    }, [subscriberId]);

    const SideMenuItems: IPageSubscriberTabMenu[] = [
        {
            title: translationSubscriberId.sideMenu.info,
            url: RoutePath.subscriberIdInfo.replace(CLIENT_ID, subscriberId),
            onClick: ({ id }) => {
                if (location.pathname !== id) {
                    dispatch(
                        logBusinessEvent({
                            eventName: ELogBusinessEventName.USER_SUBSCRIBER_CARD,
                            type: ELogBusinessEventType.common,
                        }),
                    );
                }
            },
        },
        {
            title: translationSubscriberId.sideMenu.products,
            url: RoutePath.subscriberIdProducts.replace(CLIENT_ID, subscriberId),
        },
        {
            title: translationSubscriberId.sideMenu.finances,
            url: RoutePath.subscriberIdFinances.replace(CLIENT_ID, subscriberId),
        },
        {
            title: translationSubscriberId.sideMenu.personalData,
            url: RoutePath.subscriberIdPersonalData.replace(CLIENT_ID, subscriberId),
        },
        {
            title: translationSubscriberId.sideMenu.dcRule,
            url: RoutePath.subscriberIdDcRule.replace(CLIENT_ID, subscriberId),
        },
        {
            title: translationSubscriberId.sideMenu.additionalAccounts,
            url: RoutePath.subscriberIdAdditionalAccounts.replace(CLIENT_ID, subscriberId),
        },
        {
            title: translationSubscriberId.sideMenu.manageNumber,
            url: RoutePath.subscriberIdManageNumber.replace(CLIENT_ID, subscriberId),
        },
    ];

    const renderSideMenu = (item: IPageSubscriberTabMenu) => (
        <LinkBlock key={item.url} url={item.url}>
            <SideMenuItem
                id={item.url}
                isActive={item.url === location.pathname}
                level={0}
                title={item.title}
                onClick={item.onClick}
            />
        </LinkBlock>
    );

    return (
        <Fragment>
            <PageLocale
                lazy
                fileNames={[TranslationFileNamesEnum.pageSubscriberId, TranslationFileNamesEnum.pageSubscriberMobile]}
            />

            <Title pageName={ECommonTranslationPages.subscriberId} />

            <Loader showLoader={isFetching}>
                <PageSubscriberIdStyled>
                    {error || (isSuccess && !data) ? (
                        <ErrorPage
                            src={themeIcons.errorDeliveryImage3x}
                            text={translationCommon.error.ui.text}
                            title={translationCommon.error.ui.title}
                        />
                    ) : (
                        <Fragment>
                            <PageSubscriberIdTitleStyled className="subscriber__title">
                                <EllipsisText lines={1} title={`${data?.number} - ${data?.name}`}>
                                    {data?.name ? data.number + ' - ' + data.name : data?.number}
                                </EllipsisText>
                                {
                                    isSubsInRoamingEnabled && 
                                    isRoamInfoExist &&
                                    <>
                                        <Button color='secondary' size='small' style={{ pointerEvents: 'none' }}>
                                            {translationSubscriberId.pageTitle.inRoamingBadgeText}    
                                        </Button>
                                        <PageSubscriberIdDateStyled>
                                            {roamDateString}
                                        </PageSubscriberIdDateStyled>
                                    </>
                                }
                            </PageSubscriberIdTitleStyled>
                            <div className="subscriber__side-menu">{SideMenuItems.map(renderSideMenu)}</div>
                            <div className="subscriber__content">
                                <NoticeMessage place={ENotificationPlace.subscriberId} />
                                <div className="subscriber__wrapper">
                                    <SubscriberIdContext.Provider value={{ subscriberId, userType }}>
                                        {subscriberId && isSuccess && data && children}
                                    </SubscriberIdContext.Provider>
                                </div>
                            </div>
                            <SubscriberIdFooter subscriberId={subscriberId} userType={userType} />
                        </Fragment>
                    )}
                </PageSubscriberIdStyled>
            </Loader>
        </Fragment>
    );
};

export default PageSubscriberId;
